/**
 * CANONICAL MODULE — app-platform/shared/authConfig.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/authConfig.ts`). It imports nothing from
 * any app; every app-specific fact is a parameter.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── What is fixed, and what is per-app ───────────────────────────────────────────────────────────
 * Five apps, five Azure app registrations, five client ids. **That is correct and is not a
 * divergence to standardise away.** What was accidental is everything else: four apps inherited an
 * MSAL default `postLogoutRedirectUri` (whatever page the user happened to be on), and the scope
 * lists differed for no recorded reason.
 *
 * **`clientId` is a plain string, not an env read.** Three of the five hardcode a public literal,
 * one reads an env var with a committed literal default, and Cloudflare has no env var set for any
 * of them. A module that assumed `import.meta.env` would break the real deploy story in four apps.
 * The ids are public by design — a SPA uses PKCE and ships no secret — which each app's own comment
 * already says.
 *
 * ── Scope: least privilege, decided as Q1, applied in W10 ────────────────────────────────────────
 * The target for all five is `APP_FOLDER_SCOPES` — `Files.ReadWrite.AppFolder`, not drive-wide
 * `Files.ReadWrite`. No data moves: every app already lives under `/Apps/<Name>/`, and Graph
 * resolves `special/approot` to `/Apps/<app registration display name>`, verified 2026-09-18 to
 * match for all five, trakr included, whose C# helper writes into that same real folder through the
 * filesystem.
 *
 * **But the scope cannot be flipped by this module alone, so `scopes` is a required parameter.**
 * Under `Files.ReadWrite.AppFolder` Graph **denies** absolute `/me/drive/root:/Apps/<Name>/...`
 * addressing even for that same folder. Four of the five apps address every URL that way today
 * (contently builds `/me/drive/root:/Apps/Contently/...` in a dozen places), so narrowing the scope
 * before the approot translation layer exists would 400 every read and write in the app.
 *
 * So each app flipped to `APP_FOLDER_SCOPES` in the same commit that gave it W10's approot
 * addressing. All five had by 2026-10-07; the drive-wide `DRIVE_WIDE_SCOPES` constant that bridged
 * the gap was deleted on 2026-11-01 (task 8.3: no app used it any more).
 *
 * *(Discovered on contact during W9's contently pilot, 2026-09-18. The design had this module
 * hardcoding the target scope, which would have broken the pilot app outright.)*
 *
 * `offline_access` stays — it is what makes silent renewal possible at all. `User.Read` is not in
 * the set: an app that needs `/me` adds it to the `scopes` it passes.
 */

import type { Configuration } from '@azure/msal-browser'

/**
 * The target scope for all five apps (Q1). A leaked token reaches this app's own folder and nothing
 * else on the drive. **Requires approot-relative addressing in the transport** — an app on absolute
 * `/me/drive/root:/...` URLs must not be given this scope until W10 has converted it.
 */
export const APP_FOLDER_SCOPES = ['Files.ReadWrite.AppFolder', 'offline_access'] as const

/**
 * Exported for its test. Personal Microsoft accounts only. None of these apps is a
 * work/school app.
 */
export const CONSUMERS_AUTHORITY = 'https://login.microsoftonline.com/consumers'

export interface AppAuthFacts {
  /** This app's Azure registration client id. Public by design — a SPA ships no secret. */
  clientId: string
  /**
   * `APP_FOLDER_SCOPES`, plus anything else this app really needs (e.g. `User.Read` for `/me`).
   * Required rather than defaulted, so widening an app's reach is a visible one-line change.
   */
  scopes: readonly string[]
  /** Defaults to `window.location.origin`. */
  redirectUri?: string
  /**
   * Defaults to `window.location.origin`.
   *
   * **Set it rather than inheriting MSAL's default**, which returns the user to whatever page they
   * happened to be on when they signed out — including a deep link they can no longer load.
   */
  postLogoutRedirectUri?: string
  /**
   * Per-app MSAL `system` options, passed through untouched.
   *
   * Exists for quil's PII-filtered dev logger, which is a real per-app choice rather than a
   * divergence to flatten: no other app wants MSAL logging in its console. Anything genuinely
   * common belongs in the body of `buildMsalConfig`, not here.
   */
  system?: Configuration['system']
}

function origin(): string {
  return typeof window === 'undefined' ? '/' : window.location.origin
}

export function buildMsalConfig(app: AppAuthFacts): Configuration {
  return {
    auth: {
      clientId: app.clientId,
      authority: CONSUMERS_AUTHORITY,
      redirectUri: app.redirectUri ?? origin(),
      postLogoutRedirectUri: app.postLogoutRedirectUri ?? origin(),
    },
    cache: {
      /**
       * Survives a tab close, which is what "stay signed in" means to a user.
       *
       * Note what it does *not* survive, because it drives the whole auto-login design: on MSAL v4+
       * this cache is encrypted with a key held in a session-scoped cookie, so a **browser** close
       * leaves the cached account unusable unless "Keep me signed in" was ticked — a prompt that
       * never appears in the Edge + passkey flow. See `authAutoLogin.ts`.
       */
      cacheLocation: 'localStorage',
    },
    ...(app.system ? { system: app.system } : {}),
  }
}

/** The login request. Pass the same scopes as the token request, so one consent covers both. */
export function buildLoginRequest(scopes: readonly string[]): { scopes: string[] } {
  return { scopes: [...scopes] }
}
